// ─── Group Session History Table (show phiên) ──────────────────────
// Bảng scrollable hiển thị lịch sử các phiên đã đánh của group:
//   - Cột: id phiên, 1 cột cho MỖI thành viên group (4-account), kết quả
//   - Ô của mỗi account đọc đúng như báo cáo Telegram cuối phiên:
//       dòng 1 = CÁCH ĐÁNH gộp theo cửa   → "L 2k 3D 1k 3T 1k"
//       dòng 2 = lãi lỗ + số dư sau phiên → "WIN +3.870 | 80.135"
//     (trước đây dòng 1 liệt kê từng gói lẻ kiểu "2× 1k 5" — đúng số tiền nhưng
//      không đọc ra được nhóm đã đánh cửa nào, và không có lãi lỗ)
//   - Kết quả: súc sắc (TX) hoặc 4 đĩa đỏ/trắng (XD/TL) + chẵn/lẻ/tài/xỉu/4đỏ/4trắng...
//
// Dùng chung cho cả 3 game (TX/XD/TL) — typegame quyết định nhãn cửa + cách render kết quả.

import React from 'react';
import { IGroupSessionEntry, IAccountDataResponse } from '@tool-no-hu/shared/types';
import { getGroupSessionsService } from '../../service/group';

// ─── Hook: poll group sessions ─────────────────────────────────────

/**
 * useGroupSessions — poll GET /groups/:id/sessions mỗi `intervalMs` (mặc định 3s).
 * Chỉ poll khi isOpen=true. Tự dừng khi đóng modal.
 */
export function useGroupSessions(
  groupId: string | undefined,
  isOpen: boolean,
  intervalMs: number = 3000,
) {
  const [sessions, setSessions] = React.useState<IGroupSessionEntry[]>([]);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!groupId) return;
    const data = await getGroupSessionsService(groupId, { silent: true });
    setSessions(data);
  }, [groupId]);

  React.useEffect(() => {
    if (!isOpen || !groupId) {
      setSessions([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled) return;
      setLoading(true);
      const data = await getGroupSessionsService(groupId, { silent: true });
      if (cancelled) return;
      setSessions(data);
      setLoading(false);
      timer = setTimeout(poll, intervalMs);
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [groupId, isOpen, intervalMs]);

  return { sessions, loading, refresh };
}

// ─── Bet formatting helpers ────────────────────────────────────────

/**
 * Nhãn viết tắt của từng cửa — GIỮ ĐÚNG bộ đã chốt với khách ở báo cáo Telegram
 * (`session-report.service.BET_KEY_LABEL`): Chẵn→C, Lẻ→L, Tài→T, Xỉu→X,
 * 3 đỏ 1 trắng→3D, 3 trắng 1 đỏ→3T, 4 đỏ→4D, 4 trắng→4T.
 *
 * TX dùng eid chữ (BIG/SMALL/EVEN/ODD), XD dùng eid chữ riêng, TL dùng eid số 0-5.
 */
function eidLabel(eid: string | number, typegame: string): string {
  if (typegame === 'TX') {
    switch (eid) {
      case 'BIG': return 'T';
      case 'SMALL': return 'X';
      case 'EVEN': return 'C';
      case 'ODD': return 'L';
      default: return String(eid);
    }
  }
  if (typegame === 'TL') {
    switch (Number(eid)) {
      case 0: return '4D';
      case 1: return '3D';
      case 2: return 'C';
      case 3: return '3T';
      case 4: return '4T';
      case 5: return 'L';
      default: return String(eid);
    }
  }
  // XD
  switch (eid) {
    case 'EVEN': return 'C';
    case 'ODD': return 'L';
    case 'RED_X4': return '4D';
    case 'WHITE_X4': return '4T';
    case 'RED_X3': return '3D';
    case 'WHITE_X3': return '3T';
    default: return String(eid);
  }
}

/** Tiền gọn theo nghìn (104.000 → "104k") — khớp `formatShortMoney` của báo cáo
 *  Telegram. Không chia hết 1000 thì in đầy đủ để không nuốt phần lẻ. */
function formatShortMoney(amount: number): string {
  if (amount % 1000 === 0) return `${(amount / 1000).toLocaleString('vi-VN')}k`;
  return amount.toLocaleString('vi-VN');
}

function formatMoney(amount: number): string {
  return amount.toLocaleString('vi-VN');
}

/** Lãi lỗ có dấu — số dương luôn kèm '+' để phân biệt rõ với lỗ. */
function formatSigned(delta: number): string {
  return delta > 0 ? `+${formatMoney(delta)}` : formatMoney(delta);
}

/**
 * Gộp các gói cược thành TỔNG TIỀN THEO CỬA, giữ thứ tự cửa xuất hiện đầu tiên.
 *
 * Lịch sử lưu từng gói theo mệnh giá (nhóm rải 2 gói 1k vào cửa Lẻ = 2 dòng),
 * nhưng cái khách cần đọc là "cửa nào bao nhiêu tiền" — nên cộng lại:
 * [{5,1000,×2},{1,1000,×1},{3,1000,×1}] → "L 2k 3D 1k 3T 1k".
 */
export function formatBetBreakdown(
  bets: Array<{ eid: string | number; amount: number; count: number }>,
  typegame: string,
): string {
  const totals = new Map<string, number>();
  for (const bet of bets) {
    const label = eidLabel(bet.eid, typegame);
    totals.set(label, (totals.get(label) ?? 0) + bet.amount * bet.count);
  }
  return [...totals.entries()]
    .map(([label, amount]) => `${label} ${formatShortMoney(amount)}`)
    .join(' ');
}

/** Lãi lỗ chỉ hiện khi CẢ HAI mốc số dư đều có — cùng luật với dòng account của
 *  báo cáo Telegram, thiếu một mốc thì mọi con số tính ra đều là bịa. */
function settledPnl(
  bets: IGroupSessionEntry['accountBets'][number],
): { delta: number; balanceAfter: number } | null {
  const { delta, balanceAfter } = bets;
  if (typeof delta !== 'number' || typeof balanceAfter !== 'number') return null;
  return { delta, balanceAfter };
}

/** Render 1 cell: cách đánh + lãi lỗ/số dư (chờ khi phiên chưa settle xong). */
function renderAccountCell(entry: IGroupSessionEntry, accountId: string, typegame: string) {
  const ab = entry.accountBets.find((a) => a.accountId === accountId);
  if (!ab || ab.bets.length === 0) {
    return <span className="text-slate-600">—</span>;
  }

  const pnl = settledPnl(ab);

  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap">
      <span className="font-bold text-slate-200">{formatBetBreakdown(ab.bets, typegame)}</span>
      {pnl ? (
        <span className="font-semibold">
          <span
            className={
              pnl.delta > 0
                ? 'text-emerald-400'
                : pnl.delta < 0
                  ? 'text-red-400'
                  : 'text-slate-400'
            }
          >
            {pnl.delta > 0 ? 'WIN' : pnl.delta < 0 ? 'LOSE' : 'DRAW'} {formatSigned(pnl.delta)}
          </span>
          <span className="text-slate-600"> | </span>
          <span className="text-slate-400">{formatMoney(pnl.balanceAfter)}</span>
        </span>
      ) : (
        <span className="font-semibold text-amber-400/70">Chờ lãi lỗ...</span>
      )}
    </div>
  );
}

// ─── Result rendering ──────────────────────────────────────────────

/** Render kết quả súc sắc (TX): 3 xúc xắc + total + TÀI/XỈU + CHẴN/LẺ */
function renderTaixiuResult(entry: IGroupSessionEntry) {
  const r = entry.result;
  if (!r) return <span className="text-amber-400/70 text-[10px] font-semibold">Chờ kết quả...</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <div className="flex gap-0.5">
        {r.dice.map((d, i) => (
          <span key={i} className="flex h-5 w-5 items-center justify-center rounded bg-slate-600 text-[10px] font-bold text-white">
            {d}
          </span>
        ))}
      </div>
      <span className="text-[10px] font-bold text-slate-300">={r.total}</span>
      <span className={`rounded px-1 py-0.5 text-[9px] font-bold ${r.taiXiu === 'TÀI' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
        {r.taiXiu}
      </span>
      <span className={`rounded px-1 py-0.5 text-[9px] font-bold ${r.chanLe === 'CHẴN' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
        {r.chanLe}
      </span>
    </div>
  );
}

/** Render kết quả xóc đĩa / tứ linh: 4 đĩa đỏ/trắng + CHẴN/LẺ + (4đỏ/4trắng/3đỏ/3trắng) */
function renderDiskResult(entry: IGroupSessionEntry) {
  const r = entry.result;
  if (!r) return <span className="text-amber-400/70 text-[10px] font-semibold">Chờ kết quả...</span>;
  // dice = [redCount, whiteCount, 0]
  const redCount = r.dice[0] ?? 0;
  const whiteCount = r.dice[1] ?? 0;
  const disks = Array.from({ length: 4 }, (_, i) => i < whiteCount);
  return (
    <div className="flex flex-wrap items-center gap-1">
      <div className="flex gap-0.5">
        {disks.map((isWhite, i) => (
          <span
            key={i}
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[8px] font-bold ${
              isWhite ? 'bg-slate-100 text-slate-800' : 'bg-red-600 text-white'
            }`}
          >
            {isWhite ? 'Tr' : 'Đ'}
          </span>
        ))}
      </div>
      <span className={`rounded px-1 py-0.5 text-[9px] font-bold ${r.chanLe === 'CHẴN' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
        {r.chanLe}
      </span>
      {redCount === 4 && <span className="rounded bg-red-500/20 px-1 py-0.5 text-[9px] font-bold text-red-400">4 ĐỎ</span>}
      {whiteCount === 4 && <span className="rounded bg-slate-200/20 px-1 py-0.5 text-[9px] font-bold text-slate-200">4 TRẮNG</span>}
      {redCount === 3 && <span className="rounded bg-red-500/20 px-1 py-0.5 text-[9px] font-bold text-red-400">3 ĐỎ 1 TR</span>}
      {whiteCount === 3 && <span className="rounded bg-slate-200/20 px-1 py-0.5 text-[9px] font-bold text-slate-200">3 TR 1 ĐỎ</span>}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────

export interface GroupSessionHistoryTableProps {
  groupId: string;
  typegame: string;
  members: IAccountDataResponse[];
  isOpen: boolean;
}

export const GroupSessionHistoryTable: React.FC<GroupSessionHistoryTableProps> = ({
  groupId,
  typegame,
  members,
  isOpen,
}) => {
  const { sessions, loading } = useGroupSessions(groupId, isOpen);

  // 1 cột cho MỖI thành viên group (cơ chế 4-account) — không còn cố định 2 cột.
  // Lọc member không có _id (data lỗi) để renderAccountCell luôn có key hợp lệ.
  const memberCols = members.filter((m): m is IAccountDataResponse & { _id: string } => !!m?._id);
  // Tổng số cột = Phiên + N tài khoản + Kết quả (dùng cho colSpan empty-state).
  const totalCols = memberCols.length + 2;

  // Chỉ hiển thị 10 phiên gần nhất
  const MAX_SESSIONS = 10;
  const totalSessions = sessions.length;
  const visibleSessions = sessions.slice(0, MAX_SESSIONS);
  const sessionOverflow = totalSessions - MAX_SESSIONS;

  // Header: "10/N phiên" khi vượt quá 10, ngược lại "N phiên"
  const sessionCountLabel =
    sessionOverflow > 0
      ? `${MAX_SESSIONS}/${totalSessions} phiên`
      : `${totalSessions} phiên`;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Lịch sử phiên đã đánh
        </span>
        <span className="text-[9px] text-slate-500">
          {sessionCountLabel}{loading ? ' · đang tải...' : ''}
        </span>
      </div>

      <div className="max-h-[280px] overflow-auto rounded border border-slate-700/50">
        <table className="w-full min-w-[520px] border-collapse text-left text-[11px]">
          <thead className="sticky top-0 z-10 bg-slate-800 text-slate-400">
            <tr>
              <th className="px-2 py-1.5 font-bold uppercase tracking-wider text-[9px] text-slate-500">Phiên</th>
              {memberCols.map((m) => (
                <th
                  key={m._id}
                  className="px-2 py-1.5 font-bold uppercase tracking-wider text-[9px] text-slate-500"
                  title={m.username}
                >
                  {m.username}
                </th>
              ))}
              <th className="px-2 py-1.5 font-bold uppercase tracking-wider text-[9px] text-slate-500">Kết quả</th>
            </tr>
          </thead>
          <tbody>
            {visibleSessions.length === 0 && (
              <tr>
                <td colSpan={totalCols} className="px-2 py-6 text-center text-[10px] text-slate-500">
                  Chưa có phiên nào được đánh.
                </td>
              </tr>
            )}
            {visibleSessions.map((entry) => (
              <tr key={entry.sessionId} className="border-t border-slate-700/50 hover:bg-slate-700/20">
                <td className="px-2 py-1.5 align-top">
                  <span className="font-bold text-blue-400">#{entry.sessionId}</span>
                </td>
                {memberCols.map((m) => (
                  <td key={m._id} className="px-2 py-1.5 align-top">
                    {renderAccountCell(entry, m._id, typegame)}
                  </td>
                ))}
                <td className="px-2 py-1.5 align-top">
                  {typegame === 'TX' ? renderTaixiuResult(entry) : renderDiskResult(entry)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
